import React, { useState, useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import './DonationButtons.styles.css'
import DonationButtonTooltip from './DonationButtonTooltip'

export interface DonationButtonsProps {
  heading?: string
  currencySymbol?: string
  options?: {
    amount?: number | null
    tooltip?: string
    type?: string // 'one-off' or 'monthly'
  }[]
  onAmountSelection?: (amount: number | null) => void
  minimumOneOffAmount?: number
  maximumOneOffAmount?: number
  minimumMonthlyAmount?: number
  maximumMonthlyAmount?: number
  isMegaMenu?: boolean
  otherAmountLabel?: string
  id?: string // Unique ID for each instance
}

const DonationButtons = (props: DonationButtonsProps) => {
  const {
    heading,
    options,
    currencySymbol = '$',
    onAmountSelection,
    minimumOneOffAmount,
    maximumOneOffAmount,
    minimumMonthlyAmount,
    maximumMonthlyAmount,
    isMegaMenu,
    otherAmountLabel = 'Other',
    id = 'default'
  } = props

  const [selectedRadioValue, setSelectedRadioValue] = useState<number | null>(
    null
  )
  const [inputValue, setInputValue] = useState<string>('') // Store as string for input field
  const [inputError, setInputError] = useState<string>('')
  const [activeTooltip, setActiveTooltip] = useState<{
    label: string
    target: HTMLElement
    describedInputId: string
  } | null>(null)
  const [focusedInputId, setFocusedInputId] = useState<string | null>(null)

  // Store refs to option elements for tooltip positioning
  const optionRefs = useRef<Map<number, HTMLElement>>(new Map())

  const commonTooltipId = 'donation-amount-tooltip'

  // Manage tooltip visibility based on user interaction
  useEffect(() => {
    if (!options) {
      setActiveTooltip(null)
      return
    }

    let newTooltipData: { label: string; target: HTMLElement; describedInputId: string } | null = null;

    // Find "Other" option index
    const otherIndex = options.findIndex(opt => !opt.amount);
    const otherOptionId = otherIndex !== -1 ? `donation-option-${otherIndex}-${id}` : null;

    // Show tooltip when "Other" input is focused
    if (otherIndex !== -1
      && focusedInputId === otherOptionId
      && options[otherIndex].tooltip
    ) {
      const element = optionRefs.current.get(otherIndex);
      if (element) {
        newTooltipData = {
          label: options[otherIndex].tooltip!,
          target: element,
          describedInputId: otherOptionId!
        };
      }
    } else if (selectedRadioValue !== null) {
      // Show tooltip when a radio button is selected
      const selectedIndex = options.findIndex(opt => opt.amount === selectedRadioValue);
      if (selectedIndex !== -1 && options[selectedIndex].tooltip) {
        const element = optionRefs.current.get(selectedIndex);
        if (element) {
          newTooltipData = {
            label: options[selectedIndex].tooltip!,
            target: element,
            describedInputId: `donation-option-${selectedIndex}-${id}`
          };
        }
      }
    }

    setActiveTooltip(newTooltipData);

  }, [selectedRadioValue, inputValue, inputError, options, focusedInputId, id]);


  const validateInput = (value: number, type: string) => {
    let error = ''
    const currentMin =
      type === 'one-off' ? minimumOneOffAmount : minimumMonthlyAmount
    const currentMax =
      type === 'one-off' ? maximumOneOffAmount : maximumMonthlyAmount

    if (currentMin && value < currentMin) {
      error = `Minimum amount is ${currencySymbol}${currentMin}`
    } else if (currentMax && value > currentMax) {
      error = `Maximum amount is ${currencySymbol}${currentMax}`
    }
    setInputError(error)
    return error === ''
  }

  const handleRadioChange = (amount: number) => {
    setInputError('')
    setInputValue('')
    setSelectedRadioValue(amount)
    setFocusedInputId(null)
    if (onAmountSelection) {
      onAmountSelection(amount)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const value = e.target.value
    setInputValue(value)
    setSelectedRadioValue(null)

    if (value === '') {
      setInputError('')
      if (onAmountSelection) {
        onAmountSelection(null)
      }
      return
    }

    const numericValue = parseFloat(value)
    if (!isNaN(numericValue) && numericValue > 0) {
      if (validateInput(numericValue, type)) {
        if (onAmountSelection) {
          onAmountSelection(numericValue)
        }
      } else {
        if (onAmountSelection) {
          onAmountSelection(null)
        }
      }
    } else {
      setInputError('Please enter a valid amount.')
      if (onAmountSelection) {
        onAmountSelection(null)
      }
    }
  }

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    setSelectedRadioValue(null)
    setFocusedInputId(event.currentTarget.id)

    const value = event.currentTarget.value
    if (value !== '') {
      const numericValue = parseFloat(value)
      const otherOption = options?.find(opt => !opt.amount);
      const type = otherOption?.type || 'one-off';
      if (!isNaN(numericValue) && numericValue > 0 && validateInput(numericValue, type)) {
        if (onAmountSelection) onAmountSelection(numericValue)
      } else {
        if (onAmountSelection) onAmountSelection(null)
      }
    } else {
      if (onAmountSelection) onAmountSelection(null)
    }
  }

  useEffect(() => {
    // Clear custom input when a radio button is selected
    if (selectedRadioValue !== null) {
      setInputValue('')
      setInputError('')
    }
  }, [selectedRadioValue])

  return (
    <div className='donationButtons flex flex-col pt-6'>
      {heading && (
        <p className='donationButtons__heading px-4 mb-2 text-neutral-700'>
          {heading}
        </p>
      )}
      <div className='grid grid-cols-2 gap-4 px-4 py-2 xl:grid-cols-4'>
        {options &&
          options.map((option, index) => {
            const inputId = `donation-option-${index}-${id}` // Unique ID for each option
            const isRadioSelected = selectedRadioValue === option.amount

            return (
              <div key={index} className='donationButtons__option flex items-center'>
                {option.amount ? (
                  <label
                    htmlFor={inputId}
                    ref={(el) => {
                      if (el) optionRefs.current.set(index, el)
                    }}
                    className={clsx(
                      'donationButtons__radioLabel heading-xl relative flex w-full cursor-pointer items-center justify-center rounded-[2px] px-2 py-4 text-center transition-colors duration-150 ease-in-out',
                      isRadioSelected
                        ? 'bg-brand-1 text-white'
                        : 'bg-neutral-50 text-neutral-900'
                    )}
                  >
                    <input
                      type='radio'
                      id={inputId}
                      name={'donationAmount' + id} // Unique name for each donation block
                      value={option.amount}
                      checked={isRadioSelected}
                      className='sr-only' // Visually hidden, label handles interaction
                      onChange={() => handleRadioChange(option.amount!)}
                      aria-describedby={
                        activeTooltip && activeTooltip.describedInputId === inputId
                          ? commonTooltipId
                          : undefined
                      }
                    />
                    {currencySymbol}
                    {option.amount}
                  </label>
                ) : (
                  <div
                    ref={(el) => {
                      if (el) optionRefs.current.set(index, el)
                    }}
                    className={clsx(
                      'donationButtons__inputWrapper donationInput inline-flex w-full items-center overflow-hidden rounded-[2px] border transition-colors duration-150 ease-in-out',
                      inputError
                        ? 'border-negative-500'
                        : 'border-neutral-300 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500'
                    )}
                  >
                    <span className='pl-2 pr-0.5 text-neutral-600'>
                      {currencySymbol}
                    </span>
                    <input
                      type='number'
                      id={inputId}
                      name='donationAmountOther'
                      className='w-full appearance-none bg-transparent px-2 py-4 pl-0 md:pr-0.5 text-neutral-900 focus:outline-none'
                      placeholder={otherAmountLabel}
                      value={inputValue}
                      onChange={(e) => handleInputChange(e, option.type || 'one-off')}
                      onFocus={handleInputFocus}
                      aria-describedby={
                        activeTooltip && activeTooltip.describedInputId === inputId
                          ? commonTooltipId
                          : inputError // If there's an input error, it should be described by the error message
                            ? 'donation-input-error'
                            : undefined
                      }
                      aria-invalid={!!inputError}
                      min={0}
                      step='0.01'
                    />
                  </div>
                )}
              </div>
            )
          })}
      </div>

      {activeTooltip && activeTooltip.target && (
        <DonationButtonTooltip
          id={commonTooltipId}
          tooltipLabel={activeTooltip.label}
          targetElement={activeTooltip.target}
          isMegaMenu={isMegaMenu}
        />
      )}
      {inputError && (
        <p
          id='donation-input-error'
          role='alert'
          className='mt-2 px-4 text-xs text-neutral-500'
        >
          {inputError}
        </p>
      )}
    </div>
  )
}

export default DonationButtons
